import React from "react";
import { useNavigate } from "react-router-dom";
import { Zap, CalendarDays, Cog, Truck, TrendingUp, TrendingDown, AlertTriangle, Info, Workflow, Trash2, Clock } from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { StatusBadge, DisciplineBadge } from "../components/Badges";
import { ThroughputChart } from "../components/ThroughputChart";
import { WorkloadByTechnicianChart } from "../components/WorkloadByTechnicianChart";
import { useFetch } from "../lib/useApi";
import { fmtDate, fmtNum } from "../lib/format";

function Stat({ label, value, icon: Icon, cls, foot, trend, raw }) {
  return (
    <div className="card pad stat" data-testid={`stat-${label}`}>
      <div className="lab"><span className={`ico ${cls}`}><Icon size={16} /></span>{label}</div>
      <div className="num">{raw ? value : fmtNum(value)}</div>
      {foot && (
        <div className="kpi-foot">
          {trend === "up" && <TrendingUp size={14} color="var(--green)" />}
          {trend === "down" && <TrendingDown size={14} color="var(--red)" />}
          {foot}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, loading } = useFetch("/dashboard/manager");
  if (loading || !data) return <><PageHead crumb="Manager" title="Dashboard" /><Loader /></>;

  const s = data.stats;
  const fcMax = Math.max(1, ...data.forecast.map((f) => f.count));

  return (
    <>
      <PageHead crumb="Manager" title="Dashboard" sub="Your lab at a glance — live from the calibration register." />

      <div className="grid cols-5" style={{ marginBottom: 18 }}>
        <Stat label="Active in lab" value={s.active_in_lab} icon={Zap} cls="b-blue" foot={`${fmtNum(s.ready_for_delivery)} ready for delivery`} />
        <Stat label="Calibration due · 61 days" value={s.due_61_days} icon={CalendarDays} cls="b-amber" foot={`${fmtNum(s.overdue)} overdue`} trend={s.overdue ? "down" : ""} />
        <Stat label="Awaiting parts" value={s.awaiting_parts} icon={Cog} cls="b-amber" foot={`${fmtNum(s.at_subcontractor)} at subcontractor`} />
        <Stat label="Ready for delivery" value={s.ready_for_delivery} icon={Truck} cls="b-green" foot={`${fmtNum(s.recently_scrapped)} unserviceable`} />
        <Stat label="Awaiting payment" value={s.awaiting_payment.count} icon={Clock} cls="b-gold"
          foot={<span onClick={() => navigate("/app/dispatch")} className="link" data-testid="stat-awaiting-payment-link">
            {s.awaiting_payment.oldest_days != null ? `oldest ${fmtNum(s.awaiting_payment.oldest_days)}d` : "View"} →
          </span>} />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1.3fr 1fr", marginBottom: 18 }}>
        <WorkloadByTechnicianChart />
        <div className="card pad">
          <div className="sec-h"><h3>Calibration due forecast</h3><span className="tag-soft">Next 6 weeks</span></div>
          <div className="fc">
            {data.forecast.map((f) => (
              <div className="col" key={f.label}>
                <div className="vl">{fmtNum(f.count)}</div>
                <div className="bar" style={{ height: `${(f.count / fcMax) * 100}%` }} />
                <div className="lb">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <ThroughputChart />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1.3fr 1fr" }}>
        <div>
          <div className="sec-h"><h3>Recently received & in progress</h3><span className="link" onClick={() => navigate("/app/active-lab")}>Open Active Lab →</span></div>
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Equipment</th><th>Customer</th><th>Discipline</th><th>Status</th></tr></thead>
              <tbody>
                {data.recent.length === 0 && <tr><td colSpan={4} className="t-mut">No items in the lab.</td></tr>}
                {data.recent.map((e) => (
                  <tr key={e.id}>
                    <td><div className="cell-2"><span className="t-strong">{e.description || "—"}</span><span className="s mono">{e.serial_number || e.issue_number || "—"}</span></div></td>
                    <td className="t-mut">{e.customer_name || "—"}</td>
                    <td><DisciplineBadge name={e.discipline} /></td>
                    <td><StatusBadge status={e.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="sec-h"><h3>Data quality & alerts</h3><span className="badge b-red"><span className="bd" />{data.warnings.length} flagged</span></div>
          {data.warnings.map((w, i) => (
            <div className="alert" key={`${w.title}-${i}`}>
              <div className={`ai ${w.level === "amber" ? "b-amber" : w.level === "red" ? "b-red" : "b-blue"}`}>
                {w.level === "blue" ? <Info size={15} /> : <AlertTriangle size={15} />}
              </div>
              <div><div className="at">{w.title}</div><div className="ad">{w.detail}</div></div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
